import { useApolloClient } from '@apollo/client';
import { useGetMyProfileQuery } from '@shared/graphql';
import { useCallback, useMemo, useReducer } from 'react';

import { ExamPreviewLoading } from '../../routes/preview/completed/components';

import { AuthContext, type ClientUser } from './AuthProvider';
import { AuthState, type AuthStateShape, type AuthAction } from './types';
import { mapUserProfile } from './utils';

function authReducer(state: AuthStateShape, action: AuthAction): AuthStateShape {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, authState: AuthState.LOADING };
    case 'SET_LOGGED_IN':
      return { authState: AuthState.LOGGED_IN, user: action.payload };
    case 'SET_NOT_LOGGED_IN':
      return { authState: AuthState.NOT_LOGGED_IN, user: null };
    default:
      return state;
  }
}

function PreviewAuthProvider({ children }: { children?: React.ReactNode }) {
  const { loading: isLoading } = useGetMyProfileQuery({
    onCompleted: (data) => {
      if (data?.getMyProfile) {
        const { firstname, lastname, email } = data.getMyProfile;
        const mockData = {
          username: 'exam-room-preview',
          imageUrl: '',
          title: '',
          idNumber: '-',
        };
        dispatch({
          type: 'SET_LOGGED_IN',
          payload: mapUserProfile({
            firstname: firstname,
            lastname: lastname,
            email: email,
            username: mockData.username,
            imageUrl: mockData.imageUrl,
            title: mockData.title,
            idNumber: mockData.idNumber,
          } as ClientUser),
        });
      } else {
        dispatch({ type: 'SET_NOT_LOGGED_IN' });
      }
    },
    onError: () => {
      dispatch({ type: 'SET_NOT_LOGGED_IN' });
    },
  });

  const [state, dispatch] = useReducer(authReducer, {
    authState: AuthState.LOADING,
    user: null,
  });

  const client = useApolloClient();

  const logout = useCallback(async () => {
    dispatch({ type: 'SET_NOT_LOGGED_IN' });
  }, [client]);

  const login = useCallback(async () => {
    return {
      token: 'exam-room-preview',
      message: 'success',
      success: true,
      errorCode: null,
      examineeInfo: null,
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      authState: state.authState,
      user: state.user,
      logout,
      login,
      isLoading,
    }),
    [state, logout, login, isLoading],
  );

  if (state.authState === AuthState.LOADING) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <ExamPreviewLoading variant='instruction' />
      </div>
    );
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export default PreviewAuthProvider;
